"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";

import { cn } from "@/utils/cn";

const STORAGE_KEY = "jdCreatorPopupClosed";

const JD_CREATOR_URL =
  "https://jd-creator.searchright.net?utm_source=homepage&utm_medium=organic&utm_campaign=popup&utm_content=jd";

const overlayClasses = cn(
  "fixed inset-0 z-[9999]",
  "flex flex-col items-center justify-center",
  "bg-black/45 p-4",
  "animate-modalFadeIn",
);

const modalClasses = cn(
  "relative flex w-full rounded-[20px] bg-white",
  "shadow-[0_24px_80px_rgba(0,0,0,0.18)]",
  "animate-modalSlideUp",
  "flex-col max-w-[400px] max-h-[calc(100dvh-32px-52px)] overflow-hidden",
  "tablet:flex-row tablet:max-w-[680px] tablet:max-h-none",
);

const closeBtnClasses = cn(
  "absolute right-3.5 top-3.5 z-10",
  "flex h-9 w-9 items-center justify-center",
  "rounded-full bg-white/95 cursor-pointer border-none",
  "shadow-[0_2px_8px_rgba(0,0,0,0.08)]",
  "transition-all hover:bg-[#f0f0f0] hover:scale-105",
);

const visualClasses = cn(
  "relative flex flex-col items-center justify-center text-center",
  "bg-gradient-to-br from-[#2563EB] to-[#3B82F6]",
  "w-full px-6 pt-3 pb-3",
  "tablet:w-[260px] tablet:min-w-[260px] tablet:px-8 tablet:py-12",
  "after:content-[''] after:absolute after:-bottom-px after:left-0 after:right-0",
  "after:h-5 after:rounded-t-[20px] after:bg-white",
  "tablet:after:hidden",
);

const iconBoxClasses = cn(
  "flex items-center justify-center bg-white",
  "shadow-[0_6px_20px_rgba(0,0,0,0.1)]",
  "mb-2 h-10 w-10 rounded-[10px]",
  "tablet:mb-5 tablet:h-[72px] tablet:w-[72px] tablet:rounded-[18px]",
);

const contentClasses = cn("flex flex-1 flex-col min-h-0", "tablet:px-0 tablet:pt-0 tablet:pb-0");

const descClasses = cn(
  "mb-3 text-center font-medium leading-[1.55] text-[#555]",
  "text-[14.5px]",
  "tablet:mb-7 tablet:text-left tablet:text-[19px]",
);

const ctaClasses = cn(
  "block w-full rounded-xl bg-[#1a1a1a] px-6 py-4",
  "text-center text-[15.5px] font-semibold text-white no-underline",
  "transition-all hover:bg-[#333] active:scale-[0.98]",
);

const benefitItemClasses = cn("flex items-center gap-3.5 text-[#444]", "py-2 tablet:py-3");

const benefitIconClasses = cn(
  "flex items-center justify-center rounded-[10px] bg-[#FFF8E1] text-lg",
  "h-9 w-9 min-w-[36px]",
  "tablet:h-10 tablet:w-10 tablet:min-w-[40px]",
);

const dismissBtnClasses = cn(
  "border-none bg-transparent p-1 px-0.5",
  "cursor-pointer text-[13px] text-white/70",
  "transition-colors hover:text-white",
);

function JdCreatorModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const closedDate = localStorage.getItem(STORAGE_KEY);
    if (closedDate !== new Date().toDateString()) {
      setIsOpen(true);
    }
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const closeToday = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(STORAGE_KEY, new Date().toDateString());
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  if (!isOpen) return null;

  return (
    <div
      className={overlayClasses}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className={modalClasses} role="dialog" aria-modal="true" aria-labelledby="jd-creator-modal-title">
        {/* 닫기 버튼 */}
        <button className={closeBtnClasses} onClick={close} aria-label="닫기">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            className="h-4 w-4 text-[#555]"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* 왼쪽: 비주얼 패널 */}
        <div className={visualClasses}>
          <div className="mb-4 w-full px-2 tablet:absolute tablet:left-0 tablet:right-0 tablet:top-[22px] tablet:mb-0 tablet:h-11 tablet:overflow-hidden tablet:px-0">
            <Image
              src="/image/searchright-banner-center.png"
              alt="Searchright"
              width={260}
              height={44}
              className="block h-auto w-full mix-blend-screen tablet:-mt-1"
            />
          </div>

          <div className={iconBoxClasses}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-7 w-7 text-[#1a1a1a] tablet:h-[34px] tablet:w-[34px]"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
          </div>

          <span className="mb-2 hidden tablet:inline-block rounded-[20px] bg-white/15 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[1px] text-white tablet:mb-3.5">
            NEW
          </span>

          <h2 id="jd-creator-modal-title" className="mb-3.5 text-[16px] font-bold leading-[1.4] text-white tablet:text-[21px]">
            채용공고,
            <br />
            아직도 직접<span className="tablet:hidden"> 쓰고 계신가요?</span>
            <br className="hidden tablet:block" />
            <span className="hidden tablet:inline">쓰고 계신가요?</span>
          </h2>

          <p className="mt-3 hidden text-[13px] leading-[1.5] text-white/50 tablet:block">
            AI가 만드는 채용공고
            <br />
            JD Creator
          </p>
        </div>

        {/* 오른쪽: 콘텐츠 패널 */}
        <div className={contentClasses}>
          {/* 스크롤 영역 */}
          <div className="flex-1 overflow-y-auto px-6 pt-4 pb-2 tablet:px-10 tablet:pt-7 tablet:pb-0">
            <p className={descClasses}>
              <strong className="font-bold text-[#1a1a1a] tablet:text-[20px]">JD Creator</strong>가 채용공고 작성을
              <br />
              쉽고 빠르게 도와드립니다.
            </p>

            {/* 혜택 리스트 */}
            <ul className="mb-2 list-none tablet:mb-5">
              <li className={cn(benefitItemClasses, "border-b border-[#f0f0f0]")}>
                <div className={benefitIconClasses}>💰</div>
                <div>
                  <strong className="mb-0.5 block text-[14.5px] text-[#1a1a1a]">완전 무료로 이용 가능</strong>
                  <span className="text-[13px] text-[#999]">비용 부담 없이 바로 체험</span>
                </div>
              </li>
              <li className={cn(benefitItemClasses, "border-b border-[#f0f0f0]")}>
                <div className={benefitIconClasses}>⚡</div>
                <div>
                  <strong className="mb-0.5 block text-[14.5px] text-[#1a1a1a]">3분 만에 JD 완성</strong>
                  <span className="text-[13px] text-[#999]">회사명만 입력하면 AI가 작성</span>
                </div>
              </li>
              <li className={benefitItemClasses}>
                <div className={benefitIconClasses}>✍️</div>
                <div>
                  <strong className="mb-0.5 block text-[14.5px] text-[#1a1a1a]">회원가입 불필요</strong>
                  <span className="text-[13px] text-[#999]">번거로운 절차 없이 즉시 시작</span>
                </div>
              </li>
            </ul>
          </div>

          {/* CTA — 하단 고정 */}
          <div className="px-6 pt-3 pb-5 tablet:px-10 tablet:pb-9">
            <Link href={JD_CREATOR_URL} className={ctaClasses}>
              JD Creator 무료로 사용하기
            </Link>
            <span className="mt-2.5 block text-center text-[12.5px] text-[#aaa]">가입 없이 바로 시작할 수 있어요</span>

            <div className="mt-3.5 hidden items-center justify-center gap-2 tablet:flex">
              <button className={cn(dismissBtnClasses, "text-[#ccc] hover:text-[#888]")} onClick={close}>
                다음에 할게요
              </button>
              <span className="text-[12px] text-[#ddd]">|</span>
              <button className={cn(dismissBtnClasses, "text-[#ccc] hover:text-[#888]")} onClick={closeToday}>
                오늘 하루 보지 않기
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 모바일 전용 하단 고정 dismiss 버튼 */}
      <div className="flex items-center justify-center gap-2 pt-3 tablet:hidden">
        <button className={dismissBtnClasses} onClick={close}>
          다음에 할게요
        </button>
        <span className="text-[12px] text-[#ddd]">|</span>
        <button className={dismissBtnClasses} onClick={closeToday}>
          오늘 하루 보지 않기
        </button>
      </div>
    </div>
  );
}

export default JdCreatorModal;
